import { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { getImageUrl, GalleryImage } from '@/services/api'
import { useBodyOverflow } from '@/hooks/useBodyOverflow'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

interface LightboxProps {
  images: GalleryImage[]
  currentIndex: number
  onClose: () => void
  onNavigate: (index: number) => void
}

export default function Lightbox({ images, currentIndex, onClose, onNavigate }: LightboxProps) {
  const currentImage = images[currentIndex]
  const t = useT(siteCoreMessages)
  const w = t.widgets.lightbox

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1)
    }
  }, [currentIndex, onNavigate])

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      onNavigate(currentIndex + 1)
    }
  }, [currentIndex, images.length, onNavigate])

  // Lock body scroll when lightbox is open
  useBodyOverflow(true)

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          handlePrevious()
          break
        case 'ArrowRight':
          handleNext()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, handlePrevious, handleNext])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!currentImage) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-bg-primary flex flex-col"
      onClick={handleBackdropClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-bg-primary/50 flex-shrink-0">
        <div className="text-white/80 text-sm">
          {w.position(currentIndex + 1, images.length)}
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          aria-label={w.close}
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Main Image Area - flex-1 with proper constraints */}
      <div className="flex-1 flex flex-col items-center justify-center relative px-4 sm:px-16 py-4 min-h-0 overflow-hidden">
        {/* Previous Button */}
        {currentIndex > 0 && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 w-12 h-12 flex items-center justify-center bg-bg-primary/40 hover:bg-bg-primary/60 text-white rounded-full transition-colors z-10"
            aria-label={w.previous}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Image Container - constrained to fit entire image */}
        <div className="flex-1 flex items-center justify-center w-full min-h-0">
          <img
            src={getImageUrl(currentImage.file_path) || ''}
            alt={currentImage.title || t.pages.home.galleryImageAlt}
            className="max-w-full max-h-full w-auto h-auto object-contain"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        </div>

        {/* Next Button */}
        {currentIndex < images.length - 1 && (
          <button
            onClick={handleNext}
            className="absolute right-4 w-12 h-12 flex items-center justify-center bg-bg-primary/40 hover:bg-bg-primary/60 text-white rounded-full transition-colors z-10"
            aria-label={w.next}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Title and Description - Under the image */}
        {(currentImage.title || currentImage.description) && (
          <div className="mt-4 max-w-3xl mx-auto text-center px-4 flex-shrink-0">
            {currentImage.title && (
              <h3 className="text-white text-lg sm:text-xl font-medium mb-2">{currentImage.title}</h3>
            )}
            {currentImage.description && (
              <p className="text-white/70 text-sm sm:text-base">{currentImage.description}</p>
            )}
          </div>
        )}
      </div>

      {/* Footer with tags */}
      <div className="p-4 bg-bg-primary/50 flex-shrink-0">
        <div className="max-w-2xl mx-auto">
          {/* Tags */}
          <div className="flex flex-wrap justify-center gap-2">
            {currentImage.seasons?.map(s => (
              <span
                key={`season-${s.id}`}
                className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full"
              >
                {s.year && !s.name.includes(String(s.year)) ? `${s.name} (${s.year})` : s.name}
              </span>
            ))}
            {currentImage.tournaments?.map(t => (
              <span
                key={`tournament-${t.id}`}
                className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full"
              >
                {t.name}
              </span>
            ))}
            {currentImage.teams?.map(t => (
              <span
                key={`team-${t.id}`}
                className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full"
              >
                {t.name}
              </span>
            ))}
            {currentImage.players?.map(p => (
              <span
                key={`player-${p.id}`}
                className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full"
              >
                {p.first_name} {p.last_name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="bg-bg-primary/70 p-2 overflow-x-auto flex-shrink-0">
          <div className="flex justify-center gap-2 min-w-max">
            {images.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => onNavigate(idx)}
                className={`w-16 h-16 flex-shrink-0 rounded overflow-hidden border-2 transition-colors ${
                  idx === currentIndex ? 'border-primary' : 'border-transparent hover:border-white/40'
                }`}
              >
                <img
                  src={getImageUrl(img.thumbnail_path) || ''}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}
